import mixpanel from 'mixpanel-browser';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { BuildADashTrackingEvents } from '@revelio/core';
import { FirstLoadGateSuppressionContext } from '@revelio/core/first-load-gate';

import { AIAssistantFAB } from '../CustomDash/components/AIAssistant/AIAssistantFAB';
import {
  GLOBAL_AI_FAB_RIGHT_PX,
  GLOBAL_AI_OVERLAY_Z_INDEX,
} from '../CustomDash/constants/panelWidths';
import { GlobalAIAssistantChartPreviewShell } from './global-ai-assistant-chart-preview-shell';
import { useGlobalAIAssistant } from './global-ai-assistant-context';
import { GlobalAIAssistantDrawer } from './global-ai-assistant-drawer';
import { GlobalAiAssistantEngine } from './global-ai-assistant-engine';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import { shouldHideGlobalAIAssistantForPathname } from './global-ai-assistant-visibility';
import { GlobalAiHeadlessDigestHost } from './headless-digest/global-ai-headless-digest-host';
import { useAssistantHomepageActive } from './use-assistant-homepage-active';
import { useGlobalAIAssistantFeatureEnabled } from './use-global-ai-assistant-feature-enabled';

export function GlobalAIAssistantHost() {
  const enabled = useGlobalAIAssistantFeatureEnabled();
  const { pathname } = useLocation();
  const { openDrawer, closeDrawer, dismissDrawer, isDrawerOpen, transcript } =
    useGlobalAIAssistant();
  const isHiddenPath = shouldHideGlobalAIAssistantForPathname(pathname);
  const isAssistantHome = useAssistantHomepageActive();
  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);

  useEffect(() => {
    if (isHiddenPath && (isDrawerOpen || isTurnRunning)) {
      // Explicit-hide path: closeDrawer aborts any in-flight turn. The
      // isTurnRunning arm covers a turn started INLINE on the assistant
      // homepage (drawer closed) — without it, navigating to a hidden path
      // unmounts the engine but the turn-loop's abort refs are never fired,
      // so the turn keeps running with no assistant surface left.
      closeDrawer();
    }
  }, [isHiddenPath, isDrawerOpen, isTurnRunning, closeDrawer]);

  // Landing on the homepage forces the drawer closed, so a drawer left open
  // on an earlier page can't pop back up on the first navigation away (the
  // homepage suppresses the render but not the state). State-only:
  // closeDrawer would abort a turn that keeps streaming inline here.
  useEffect(() => {
    if (isAssistantHome && isDrawerOpen) {
      dismissDrawer();
    }
  }, [isAssistantHome, isDrawerOpen, dismissDrawer]);

  if (!enabled) {
    return null;
  }

  if (isHiddenPath) {
    return null;
  }

  // The headless engine mounts whenever an assistant surface is active OR a
  // conversation exists, so an in-flight turn (and its cross-navigation
  // continuations) survives skin swaps and route changes. `isTurnRunning` is
  // included defensively so a SILENT turn (which skips appendTranscript) on an
  // empty transcript can't leave `hasConversation` false and unmount the engine
  // mid-turn — keeping this aligned with `hasConversation` in `Homepage`.
  // Hidden paths still unmount it — the closeDrawer effect above already
  // aborted the turn, and the engine's own abort ref covers the taxonomy wait.
  // Note: leaving the assistant homepage does NOT auto-open the small drawer
  // (product call 2026-07-15) — the conversation isn't lost, because this
  // keeps the engine mounted; the FAB (or returning home) picks it back up.
  const hasConversation = transcript.length > 0;
  const engineActive =
    isDrawerOpen || isAssistantHome || hasConversation || isTurnRunning;

  return (
    // The assistant's surfaces are siblings of the visible page, never the
    // first page themselves. Suppress first-load-gate blocking for the whole
    // subtree so a background mount here — above all the headless-digest
    // warm-up's FilterProvider, which loads the taxonomy trees offscreen —
    // can't hold the first-load overlay open until those requests finish.
    <FirstLoadGateSuppressionContext.Provider value={true}>
      <GlobalAIAssistantChartPreviewShell />
      <GlobalAiHeadlessDigestHost />
      {engineActive ? <GlobalAiAssistantEngine /> : null}
      {!isAssistantHome && (
        <AIAssistantFAB
          isVisible
          fixedRightPx={GLOBAL_AI_FAB_RIGHT_PX}
          zIndex={GLOBAL_AI_OVERLAY_Z_INDEX}
          tooltipLabel={isDrawerOpen ? 'Close AI chat' : 'Open AI chat'}
          aria-label={isDrawerOpen ? 'Close AI chat' : 'Open AI chat'}
          onClick={() => {
            if (isDrawerOpen) {
              closeDrawer();
              return;
            }
            mixpanel.track(BuildADashTrackingEvents.GLOBAL_AI_ASSISTANT_OPEN);
            openDrawer();
          }}
        />
      )}
      {isDrawerOpen && !isAssistantHome ? <GlobalAIAssistantDrawer /> : null}
    </FirstLoadGateSuppressionContext.Provider>
  );
}
